import is from '@sindresorhus/is';
import { useEffect, useReducer } from 'react';

import type { JobArchitectureMetadata, PrimaryGoalType } from '~/shared/data-contract/job-architecture';

type GoalSelectionState = {
    readonly dirty: boolean;
    readonly hydratedId: string | undefined;
    readonly otherText: string;
    readonly selectedGoals: PrimaryGoalType[];
};

const initialState: GoalSelectionState = {
    dirty: false,
    hydratedId: undefined,
    otherText: '',
    selectedGoals: [],
};

type GoalSelectionAction =
    | { readonly type: 'hydrate'; readonly id: string; readonly metadata: JobArchitectureMetadata | null }
    | { readonly type: 'reset' }
    | { readonly type: 'set-other-text'; readonly otherText: string }
    | { readonly type: 'toggle-goal'; readonly goal: PrimaryGoalType };

const deriveFromMetadata = (
    metadata: JobArchitectureMetadata | null,
): Pick<GoalSelectionState, 'otherText' | 'selectedGoals'> => ({
    otherText: is.nonEmptyString(metadata?.primaryGoalOtherText) ? metadata.primaryGoalOtherText : '',
    selectedGoals: metadata?.primaryGoals ?? [],
});

const reducer = (state: GoalSelectionState, action: GoalSelectionAction): GoalSelectionState => {
    switch (action.type) {
        case 'hydrate': {
            if (state.hydratedId === action.id && state.dirty) return state;
            return { ...deriveFromMetadata(action.metadata), dirty: false, hydratedId: action.id };
        }
        case 'reset': {
            return initialState;
        }
        case 'set-other-text': {
            return { ...state, dirty: true, otherText: action.otherText };
        }
        case 'toggle-goal': {
            return {
                ...state,
                dirty: true,
                selectedGoals: state.selectedGoals.includes(action.goal)
                    ? state.selectedGoals.filter((g) => g !== action.goal)
                    : [...state.selectedGoals, action.goal],
            };
        }
    }
};

export const useGoalSelection = (
    jobArchitectureId: string | undefined,
    metadata: JobArchitectureMetadata | null | undefined,
) => {
    const [state, dispatch] = useReducer(reducer, initialState);

    useEffect(() => {
        if (is.undefined(jobArchitectureId) || is.undefined(metadata)) {
            dispatch({ type: 'reset' });
            return;
        }
        dispatch({ type: 'hydrate', id: jobArchitectureId, metadata });
    }, [jobArchitectureId, metadata]);

    const toggleGoal = (goal: PrimaryGoalType): void => {
        dispatch({ type: 'toggle-goal', goal });
    };

    const setOtherText = (otherText: string): void => {
        dispatch({ type: 'set-other-text', otherText });
    };

    return {
        isOtherSelected: state.selectedGoals.includes('other'),
        otherText: state.otherText,
        selectedGoals: state.selectedGoals,
        setOtherText,
        toggleGoal,
    };
};
